import { collection, query, where, getDocs, writeBatch } from 'firebase/firestore';
import { db } from '../services/firebase';

/**
 * Delete all engagement documents for a user from tbl_engagement.
 * Tries multiple identifiers to be resilient to historical data shapes.
 *
 * `onError` is OPTIONAL and purely additive: the return value stays the deleted
 * count, so the existing callers (utils/cleanupCoordinator.js,
 * providers/BrokerConnectionsProvider.jsx) are unaffected. It exists because the
 * per-identifier failures below are logged and swallowed on purpose — one bad
 * identifier must not abort the rest — which leaves a caller unable to tell a
 * total failure from "this user had no rows", both of which return 0. The standing
 * authorization revoke flow (pages/account/index.jsx) has to tell those apart:
 * revoking must not report "your engagement is cleared" when it is not.
 */
export const deleteEngagementsForUser = async ({ userId, email, username, onError }) => {
  let totalDeleted = 0;
  const report = (e) => { if (typeof onError === 'function') onError(e); };

  const deleteByQuery = async (q) => {
    const snap = await getDocs(q);
    if (snap.empty) return 0;
    const batch = writeBatch(db);
    snap.docs.forEach((d) => batch.delete(d.ref));
    await batch.commit();
    return snap.size;
  };

  // 1) Match by userId
  if (userId) {
    try {
      totalDeleted += await deleteByQuery(query(collection(db, 'tbl_engagement'), where('userId', '==', userId)));
    } catch (e) {
      console.warn('Failed deleting engagements by userId:', e);
      report(e);
    }
  }

  // 2) Match by email
  if (email) {
    try {
      totalDeleted += await deleteByQuery(query(collection(db, 'tbl_engagement'), where('email', '==', email)));
      totalDeleted += await deleteByQuery(query(collection(db, 'tbl_engagement'), where('userEmail', '==', email)));
    } catch (e) {
      console.warn('Failed deleting engagements by email:', e);
      report(e);
    }
  }

  // 3) Match by username
  if (username) {
    try {
      totalDeleted += await deleteByQuery(query(collection(db, 'tbl_engagement'), where('username', '==', username)));
      totalDeleted += await deleteByQuery(query(collection(db, 'tbl_engagement'), where('userName', '==', username)));
    } catch (e) {
      console.warn('Failed deleting engagements by username:', e);
      report(e);
    }
  }

  return totalDeleted;
};

export default deleteEngagementsForUser;


